import { useState, useEffect } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  FormControl,
  OutlinedInput,
  MenuItem,
  Select,
  InputLabel,
  alpha,
} from '@mui/material';
import { useRouteLoaderData } from 'react-router-dom';
import FormButtonControls from '@/components/forms/formButtonControls/FormButtonControls';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';

export default function GeneralUserSubscriptions({
  app,
  next,
  back,
  isLastStep,
  isFirstStep,
  handleCancel,
}) {
  const { users } = useRouteLoaderData('auth');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [error, setError] = useState(true);
  const [allowNoUsers, setAllowNoUsers] = useState(false);

  const handleUserChange = (event) => {
    setSelectedUsers(event.target.value);
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.filter((id) => id !== userId),
    );
  };

  const handleCheckboxChange = (event) => {
    setAllowNoUsers(event.target.checked);
  };

  const handleSave = () => {
    if (selectedUsers.length === 0 && !allowNoUsers) {
      setError(true);
    } else {
      setError(false);
      next({ users: selectedUsers });
    }
  };

  useEffect(() => {
    if (allowNoUsers) {
      setSelectedUsers([]);
      setError(false);
    } else if (selectedUsers.length === 0) {
      setError(true);
    } else {
      setError(false);
    }
  }, [allowNoUsers, selectedUsers.length]);

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: '20%',
      },
    },
  };

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <CardHeader
        avatar={<PeopleAltIcon color="secondary" />}
        title={<Typography variant="h6">Benutzerzuweisung</Typography>}
        subheader={`Für ${app.title}`}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.secondary.light, 0.1),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />

      <CardContent sx={{ pt: 3, pb: 2, flex: 1 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Wählen Sie alle Benutzer aus, die eine Lizenz für {app.title} erhalten
          sollen.
        </Typography>

        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <InputLabel id="user-select-label">Benutzer</InputLabel>
          <Select
            labelId="user-select-label"
            id="user-select"
            multiple
            value={selectedUsers}
            onChange={handleUserChange}
            MenuProps={MenuProps}
            input={<OutlinedInput id="select-multiple-chip" label="Benutzer" />}
            disabled={allowNoUsers}
          >
            {users.map((user) => (
              <MenuItem key={user._id} value={user._id}>
                {`${user.firstName} ${user.lastName}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={allowNoUsers}
              onChange={handleCheckboxChange}
              name="allowNoUsers"
              color="secondary"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Keine Benutzer auswählen
            </Typography>
          }
        />

        {error && selectedUsers.length === 0 && !allowNoUsers && (
          <Typography
            color="error"
            variant="caption"
            sx={{ display: 'block', mt: 1 }}
          >
            Bitte wählen Sie mindestens einen Benutzer aus oder aktivieren Sie
            die Option Keine Benutzer auswählen.
          </Typography>
        )}

        {selectedUsers.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Ausgewählte Benutzer:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mt: 1,
                p: 2,
                borderRadius: 1,
                bgcolor: (theme) =>
                  alpha(theme.palette.background.default, 0.7),
              }}
            >
              {selectedUsers.map((userId) => {
                const user = users.find((u) => u._id === userId);
                if (!user) return null;
                return (
                  <Chip
                    key={user._id}
                    label={`${user.firstName} ${user.lastName}`}
                    onDelete={() => handleRemoveUser(userId)}
                    color="secondary"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                );
              })}
            </Box>
          </Box>
        )}
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
        <FormButtonControls
          next={next && handleSave}
          back={back}
          cancel={handleCancel}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          disabled={error}
        />
      </CardActions>
    </Card>
  );
}
